import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import useDebounce from '@/common/hooks/useDebounce'
import { EquipmentsTransformer } from '@/common/services/equipmentTransformer'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useEquipmentModelEquipments } from '@/features/modelIntegrationCodes/hooks/useEquipmentModelEquipments'
import { useLinkIntegrationCode } from '@/features/modelIntegrationCodes/hooks/useLinkIntegrationCode'
import { useModelIntegrationCode } from '@/features/modelIntegrationCodes/hooks/useModelIntegrationCode'
import InfocarInfo from '@/features/modelIntegrationCodes/pages/Show/components/InfocarInfo'
import ModelsSearch, { ModelsSearchTabValue } from '@/features/modelIntegrationCodes/pages/Show/components/ModelsSearch'
import SelectedModel from '@/features/modelIntegrationCodes/pages/Show/components/SelectedModel'
import { ModelIntegrationCodesTransformer } from '@/features/modelIntegrationCodes/services/modelIntegrationCodesTransformer'
import {
  IntegrationCodeMatch,
  integrationCodeMatchKey,
} from '@/features/modelIntegrationCodes/types/suggestedModel.type'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const ModelIntegrationCodesShow = () => {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { data: modelIntegrationCode, error = {} as any, refetch } = useModelIntegrationCode(id || '')

  const [activeTab, setActiveTab] = useState<ModelsSearchTabValue>('similar')
  const [selectedModel, setSelectedModel] = useState<IntegrationCodeMatch>()
  const [selectedEquipment, setSelectedEquipment] = useState<Option>()
  const [equipmentSearch, setEquipmentSearch, instantEquipmentSearch] = useDebounce('')
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: equipments = [],
    isFetching: isFetchingEquipments,
    dataUpdatedAt: equipmentsUpdatedAt,
  } = useEquipmentModelEquipments({
    modelIntegrationCodeId: id,
    filters: { search: equipmentSearch },
    mapper: Mapper.mapWithTransformer({
      transformer: EquipmentsTransformer.optionsTransformer({
        icon: false,
      }),
    }),
  })

  useEffect(() => {
    if (selectedEquipment) return
    if (instantEquipmentSearch) return
    const firstEquipment = equipments[0]
    if (!firstEquipment) return
    setSelectedEquipment(firstEquipment)
  }, [equipmentsUpdatedAt, selectedEquipment, instantEquipmentSearch])

  const handleEquipmentChange = (equipment: Option) => {
    setSelectedEquipment(equipment)
    setSelectedModel(undefined)
  }

  const { mutate: linkIntegrationCode, isPending: isConfirming } = useLinkIntegrationCode({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modelIntegrationCodes', 'pending'] })
      queryClient.invalidateQueries({ queryKey: ['modelIntegrationCodes', 'show', id] })

      navigate(`/equipment-models/${id}/integrations`, { replace: true })
    },
    onError: FormTransformers.errorsTransformer({
      setMessages,
      transformer: ModelIntegrationCodesTransformer.errorsTransformer(),
    }),
  })

  const errorStatus = (error as any)?.status ?? (error as any)?.response?.status

  const handleConfirm = () => {
    if (!id || !selectedModel) return
    linkIntegrationCode({
      id,
      provider: selectedModel.provider,
      integration_code_value: selectedModel.integration_code_value,
    })
  }

  const selectedKey = selectedModel ? integrationCodeMatchKey(selectedModel) : undefined

  return (
    <div className="inner-screen gap-2">
      <div className="flex w-full items-center">
        <GoBackButton fallback="/model-integration-codes" searchLastRouteIgnoringFilters />
        <p className="text-white-400 truncate">Homologação de Modelos de equipamento</p>
        <ChevronsRight color="#ffffffb3" className="shrink-0" />
        <p className="font-normal truncate">{modelIntegrationCode?.display_name || '--'}</p>
      </div>

      <ErrorMessageWrapper statusCode={errorStatus} refetchFunction={refetch}>
        <div className="flex gap-2 flex-1 min-h-0">
          <ModelsSearch
            modelIntegrationCodeId={id}
            equipmentId={selectedEquipment?.value}
            activeTab={activeTab}
            onTabChange={(tab) => {
              setActiveTab(tab)
              setSelectedModel(undefined)
            }}
            selectedKey={selectedKey}
            onSelect={setSelectedModel}
          />

          <div className="flex-1 flex flex-col gap-2 min-h-0 min-w-0">
            <InfocarInfo
              modelIntegrationCodeId={id}
              equipments={equipments}
              selectedOption={selectedEquipment}
              onEquipmentChange={handleEquipmentChange}
              isLoadingEquipments={isFetchingEquipments}
              onEquipmentSearch={setEquipmentSearch}
            />

            <SelectedModel selectedModel={selectedModel} onConfirm={handleConfirm} isConfirming={isConfirming} />
          </div>
        </div>
      </ErrorMessageWrapper>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </div>
  )
}

export default ModelIntegrationCodesShow
